import React, { useState, useEffect, useRef } from 'react';
import HeroSection from './HeroSection';
import CategoriesSection from './CategoriesSection';
import FeaturedCourses from './FeaturedCourses';
import CallToAction from './CallToAction';
import Carousel from './Carousel';
import CourseCard from './CourseCard';
import { getAllCourses } from '../api';
import { courseCategories, courseMatchesCategory } from '../utilities/courseCategories';

const Home = ({ onNavigate, onSelectCourse, selectedCategory, onSelectCategory }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const coursesSectionRef = useRef(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await getAllCourses();
        if (response.success) {
          setCourses(response.data);
        } else {
          setError(response.message || 'Error al cargar cursos');
        }
      } catch (err) {
        setError(err.message || 'Error de conexion');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  if (loading) return <div className="text-center py-8">Cargando cursos...</div>;
  if (error) return <div className="text-center py-8 text-red-500">Error: {error}</div>;

  const categoriesWithCounts = courseCategories.map((category) => ({
    ...category,
    courseCount: courses.filter((course) => courseMatchesCategory(course, category.name)).length
  }));

  const filteredCourses = selectedCategory
    ? courses.filter((course) => courseMatchesCategory(course, selectedCategory))
    : courses;

  const handleCourseClick = (courseId) => {
    onSelectCourse(courseId);
    onNavigate('courseDetail');
  };

  const handleViewCourses = () => {
    coursesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <HeroSection
        onStartNow={() => onNavigate('login')}
        onViewCourses={handleViewCourses}
      />
      <Carousel onNavigate={onNavigate} />
      <CategoriesSection
        categories={categoriesWithCounts}
        selectedCategory={selectedCategory}
        onSelectCategory={onSelectCategory}
      />
      <section ref={coursesSectionRef} className="pb-12">
        <FeaturedCourses
          courses={selectedCategory ? filteredCourses : courses}
          onNavigate={onNavigate}
          onSelectCourse={onSelectCourse}
        />
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-8">
            <div>
              <h2 className="text-3xl font-bold">
                {selectedCategory ? `Cursos de ${selectedCategory}` : 'Todos los cursos por rubro'}
              </h2>
              <p className="text-gray-600 mt-2">
                {filteredCourses.length} curso{filteredCourses.length === 1 ? '' : 's'} disponible{filteredCourses.length === 1 ? '' : 's'}
              </p>
            </div>
            {selectedCategory ? (
              <button
                type="button"
                onClick={() => onSelectCategory(null)}
                className="px-4 py-2 rounded-full border border-gray-300 font-semibold text-gray-700 hover:bg-gray-50"
              >
                Quitar filtro
              </button>
            ) : null}
          </div>

          {filteredCourses.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  onClick={() => handleCourseClick(course.id)}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-8 text-center text-gray-600">
              No hay cursos publicados para esta categoria todavia.
            </div>
          )}
        </div>
      </section>
      <CallToAction onNavigate={onNavigate} />
    </>
  );
};

export default Home;
